// @flow

import keycode from 'keycode';
// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React /* eslint-disable-line no-unused-vars */, {
  type Node,
  useState,
} from 'react';

import SkipLink from '@zapier/design-system-patterns/src/components/UniversalNav/SkipLink';
import { UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH } from '@zapier/design-system-patterns/src/components/UniversalNav/UniversalSidebar';
import { Zindexes } from '@zapier/theme';
import { useLink } from '@zapier/common-hooks';
import {
  Animation,
  Colors,
  Link,
  useClickOutside,
} from '@zapier/design-system';
import { ServiceTypeahead, ZapierLogo } from '@zapier/design-system-patterns';
import {
  UniversalTopbarLinks,
  skipLinkHref,
} from '@zapier/design-system-patterns/src/components/UniversalNav/constants';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import AccountActions from './AccountActions';
import SearchToggler from './SearchToggler';
import UniversalTopbarLink from './UniversalTopbarLink';

import type { UniversalNavLinkType } from '@zapier/design-system-patterns/src/components/UniversalNav/types';

/** @jsx isolateComponent */

export const UNIVERSAL_TOPBAR_CONDENSED_HEIGHT = 60;
export const UNIVERSAL_TOPBAR_HEIGHT = 70;

export type Props = {|
  /*
   * Indicates whether a slot exists to render the collapse button.
   * Doesn't render the collapse button itself though, since in some
   * instances `UniversalSidebar` may overlap the empty slot.
   */
  hasCollapseButtonSlot: boolean,
  /*
   * Indicates whether search fills the entire topbar or not.
   */
  isSearchFullWidth: boolean,
  /*
   * How wide the sidebar is in px.
   */
  sidebarWidth: number,
  /**
   * An array of links to be rendered inside the main navigation.
   */
  links: Array<UniversalNavLinkType>,
  /**
   * Zapier logo label for link
   */
  logoAriaLabel: string,
  /**
   * Zapier logo link
   */
  logoHref: string,
  /**
   * Optional handler for when anywhere on the top nav is clicked.
   */
  onClick?: (SyntheticMouseEvent<HTMLElement>) => void,
  /**
   * Zapier logo link callback
   */
  onLogoClick?: () => void,
  /**
   * Renders an account menu if provided. This overrides the default account actions menu (Sign In, Sign Up)
   */
  renderAccountMenu: (props: Props) => Node,
  /**
   * Renders notifications component if provided
   */
  renderNotifications: (props: Props) => Node,
  /**
   * Render the sidebar component if provided
   */
  renderSidebar?: (props: Props) => Node,
  /**
   * Currently selected link inside links
   */
  selectedHref: string,
  /**
   * If true, renders submenu if present in a link
   */
  shouldRenderSubmenu: boolean,
  /**
   * Size of the topbar which adjusts its height, padding, etc.
   */
  size: 'small' | 'medium',
  /**
   * Anchor to the main content area of the page.
   * **The page must contain a node with the `id` in this prop!**
   */
  skipLinkHref: string,
|};

const MAIN_GRID_GAP = 20;

const Styles = labelStyles(__filename, {
  root: props => [
    {
      alignItems: 'stretch',
      display: 'grid',
      gridTemplateColumns: 'max-content auto max-content',
      position: 'relative', // Needed for submenu dropdown
      zIndex: Zindexes.universalNav,
      width: '100%',
      backgroundColor: Colors.white,
      borderBottom: `1px solid ${Colors.grey200}`,
    },
    withModifier(
      props.size,
      {
        small: {
          gap: 5,
          height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
          padding: 5,
        },
        medium: {
          gap: MAIN_GRID_GAP,
          height: UNIVERSAL_TOPBAR_HEIGHT,
          padding: 10,
        },
      }[props.size]
    ),
  ],
  group: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: 'inherit',
  },
  sidebarSlot: (props: Props) => [
    withModifier(
      props.size,
      {
        small: {
          minWidth: 'auto',
        },
        medium: {
          minWidth: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH - MAIN_GRID_GAP,
        },
      }[props.size]
    ),
  ],
  mainNavigationContent: (props: Props) => [
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    !props.isSearchFullWidth &&
      withModifier('offsetParent', {
        position: 'relative',
      }),
  ],
  searchForm: (props: Props, { isSearching }) => [
    {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: Animation.transitionValue,
      opacity: 0,
      pointerEvents: 'none',
      transform: 'scaleX(0)',
    },
    isSearching &&
      withModifier('searching', {
        opacity: 1,
        pointerEvents: 'auto',
        transform: 'scaleX(1)',
      }),
    props.isSearchFullWidth &&
      withModifier('fullWidth', {
        // Ditch the `transform` effect on small screens.
        transform: 'none',
        backgroundColor: Colors.white,
      }),
  ],
  searchFormInner: (props: Props) => [
    {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 10,
      width: '100%',
      maxWidth: 500,
    },
    props.isSearchFullWidth &&
      withModifier('fullWidth', {
        padding: `0 10px 0 calc(${props.sidebarWidth}px + 10px)`,
        width: '100%',
      }),
  ],
  searchToggler: ({ isSearching }) => [
    {
      transition: Animation.transitionValue,
    },
    isSearching &&
      withModifier('hidden', {
        opacity: 0.2,
        pointerEvents: 'none',
      }),
  ],
  navLinks: {
    // Since we do not have a limit on the number of links,
    // this neatly wraps the nav section to prevent column overlap
    overflow: 'auto',
    lineHeight: 1.5,
  },
  navLinksList: (props: Props) => ({
    display: 'grid',
    gridGap: 40,
    gridTemplateColumns: `repeat(${props.links.length}, max-content)`,
  }),
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

const Links = (props: Props) => {
  const links = props.links.map(link => (
    <li key={link.href}>
      <UniversalTopbarLink
        href={link.href}
        isSelected={props.selectedHref === link.href}
        shouldRenderSubmenu={props.shouldRenderSubmenu}
        submenu={link.submenu}
      >
        {link.text}
      </UniversalTopbarLink>
    </li>
  ));
  return (
    <nav css={Styles.navLinks}>
      <ul css={Styles.navLinksList(props)}>{links}</ul>
    </nav>
  );
};

/**
 * Renders universal navigation top bar with Zapier logo, search,
 * links and account actions.
 */
const UniversalTopbar = (props: Props) => {
  const sidebar = props.renderSidebar && props.renderSidebar(props);
  const logoSize = {
    small: { width: 68, height: 31 },
    medium: { width: 80, height: 37 },
  }[props.size];
  // Used to tie search `input` to its search toggler `label`.
  const searchInputId = 'universal-nav-app-search';
  // Track whether the user is currently searching.
  const [isSearching, setIsSearching] = useState(false);
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => {
      setIsSearching(false);
    },
  });
  const onEscape = e => {
    if (e.keyCode === keycode('escape')) {
      setIsSearching(false);
    }
  };
  const { routeTo } = useLink();
  return (
    // onClick here exists to support behaviour logic might needed by other components of the Nav.
    //  E.g; Closing the sidebar opened by Topbar if another element on Topbar is clicked.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <header
      css={Styles.root(props)}
      data-testid="universalTopbar"
      onClick={props.onClick}
    >
      <div css={Styles.group}>
        {sidebar && <div css={Styles.sidebarSlot(props)}>{sidebar}</div>}
        <div css={Styles.logoLinksWrapper}>
          <Link
            ariaLabel={props.logoAriaLabel}
            href={props.logoHref}
            onClick={props.onLogoClick}
          >
            <ZapierLogo {...logoSize} />
          </Link>
          <div css={Styles.skipLink}>
            <SkipLink href={props.skipLinkHref} />
          </div>
        </div>
      </div>
      <div css={Styles.mainNavigationContent(props)}>
        {props.links.length > 0 && <Links {...props} />}
        <div css={Styles.searchForm(props, { isSearching })}>
          <div css={Styles.searchFormInner(props)}>
            {/* eslint-disable-next-line */}
            <div onKeyUp={onEscape} ref={setClickOutsideNode}>
              <ServiceTypeahead
                excludeUpcoming={false}
                getIconUrlForItem={item => item.iconUrl}
                getLabelForItem={item => item.label}
                inputId={searchInputId}
                menuAriaLabel="List of available apps"
                onChange={app => {
                  if (app && app.slug && routeTo) {
                    const url = `/apps/${app.slug}`;
                    routeTo(url);
                  }
                }}
                placeholder="Search by App"
              />
            </div>
            <SearchToggler ariaLabel="Close search" isSearching={true} />
          </div>
        </div>
      </div>
      <div css={Styles.group}>
        {/* eslint-disable-next-line */}
        <div
          css={Styles.searchToggler({ isSearching })}
          data-testid="searchToggler"
          onClick={() => setIsSearching(true)}
        >
          <SearchToggler
            id={searchInputId}
            isSearching={false}
            label="Search apps…"
          />
        </div>
        {props.renderNotifications(props)}
        {props.renderAccountMenu(props)}
      </div>
    </header>
  );
};

UniversalTopbar.defaultProps = {
  hasCollapseButtonSlot: false,
  links: UniversalTopbarLinks,
  logoAriaLabel: 'Go to the Zapier Homepage',
  logoHref: '/',
  onLogoClick: () => {},
  renderAccountMenu: (props: Props) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications: () => null,
  selectedHref: '',
  shouldRenderSubmenu: false,
  size: 'medium',
  skipLinkHref,
};

export default UniversalTopbar;
